import React, { memo } from 'react';

import { Stack } from '@/components/layout';
import { Image, Text } from '@/components/ui';
import type { CoGuardianProfileVM } from '@/features/guardian-player/types';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

type ProfileIdentityProps = {
  profile: CoGuardianProfileVM;
  /** Primary-guardian-only line, e.g. "Created profile since Sep 2023" (from the player). */
  createdProfileLabel?: string;
};

/** Avatar + name + role/join date + email block. Centered on phone, left-aligned on tablet. */
function ProfileIdentity({ profile, createdProfileLabel }: ProfileIdentityProps) {
  // For the circle owner, the "created profile" date replaces the "joined since" line.
  const secondaryLabel = createdProfileLabel ?? profile.joinedLabel;
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const initial = profile.name.trim().charAt(0).toUpperCase();
  const avatarSize = scale(100);

  const avatar = (
    <Stack
      align="center"
      justify="center"
      width={avatarSize}
      height={avatarSize}
      borderRadius={avatarSize / 2}
      backgroundColor={guardianColors['8']}
      style={!profile.avatarUrl && styles.border}
      overflow="hidden">
      {profile.avatarUrl ? (
        <Image
          source={{ uri: profile.avatarUrl }}
          width={avatarSize}
          height={avatarSize}
          circular
          resizeMode="cover"
        />
      ) : (
        <Text variant="Font Guardian App/Heading/Heading 20" style={styles.initial}>
          {initial}
        </Text>
      )}
    </Stack>
  );

  const meta = (
    <Stack
      gap={scale(8)}
      align={isTablet ? 'flex-start' : 'center'}
      flex={isTablet ? 1 : undefined}>
      <Text
        variant="Font Guardian App/Heading/Heading H6 18px"
        numberOfLines={1}
        style={styles.name}>
        {profile.name}
      </Text>
      <Stack align={isTablet ? 'flex-start' : 'center'} gap={scale({ phone: 0, tablet: 8 })}>
        <Stack direction="row" align="center" gap={scale(6)}>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
            numberOfLines={1}
            style={styles.role}>
            {profile.roleLabel}
          </Text>
          {secondaryLabel ? (
            <>
              <Stack style={styles.dot} />
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                numberOfLines={1}
                style={styles.joinedLabel}>
                {secondaryLabel}
              </Text>
            </>
          ) : null}
        </Stack>
        {profile.email ? (
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
            numberOfLines={1}
            style={styles.email}>
            {profile.email}
          </Text>
        ) : null}
      </Stack>
    </Stack>
  );

  if (isTablet) {
    return (
      <Stack direction="row" align="center" gap={scale(24)}>
        {avatar}
        {meta}
      </Stack>
    );
  }

  return (
    <Stack align="center" gap={scale(8)}>
      {avatar}
      {meta}
    </Stack>
  );
}

export default memo(ProfileIdentity);

const useStyles = createStyles(({ scale }) => ({
  initial: {
    color: guardianColors.Blue,
  },
  name: {
    color: guardianColors['10'],
  },
  role: {
    color: guardianColors['Main/Main'],
  },
  joinedLabel: {
    color: guardianColors['6'],
  },
  dot: {
    width: scale(2),
    height: scale(2),
    borderRadius: scale(1),
    backgroundColor: guardianColors['6'],
  },
  email: {
    color: guardianColors['Main/Main'],
  },
  border: {
    borderWidth: scale(3),
    borderColor: colors['0'],
  },
}));
